import React, {useRef, useState, useMemo, useEffect} from "react";
import {Form} from "react-router-dom";
import {toast} from "react-toastify";
import AddStageMutation from "./mutation/AddStage";
import {GlobalStyles} from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import {alphaFromHex} from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import {Divider} from "@mui/material";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function AddStageModal({stages, setStages, modalElem, activeSeasonData}) {
    const [addStage] = AddStageMutation();
    const colors = useThemeColors();
    const {t} = useTranslation();

    const stageNameInput = useRef(null);
    const stageTypeInput = useRef(null);
    const minGamesRequiredInput = useRef(null);
    const qtyTeamsQualified = useRef(null);
    const qtyTeamsAdditional = useRef(null);
    const gamesModeInput = useRef(null);

    const [stageType, setStageType] = useState("");

    // ✅ Modal-open safety (prevents “page locked” if something closes oddly)
    useEffect(() => {
        const el = document.getElementById("add-stage-modal");
        if (!el) return;

        const cleanup = () => {
            document.body.classList.remove("modal-open");
            document.body.style.removeProperty("padding-right");
            document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
        };

        el.addEventListener("hidden.bs.modal", cleanup);
        return () => el.removeEventListener("hidden.bs.modal", cleanup);
    }, []);

    const ui = useMemo(() => {
        return {
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {display: "flex", alignItems: "center", gap: 10, minWidth: 0},
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: colors.textOnPrimary,
            },
            body: {padding: 14},
            sectionCard: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            label: {fontSize: 12.5, fontWeight: 900, color: "rgba(20,21,26,0.70)", marginBottom: 6},
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                padding: "0px 12px",
                fontWeight: 700,
            },
            helper: {marginTop: 6, fontSize: 12, color: "rgba(20,21,26,0.55)", fontWeight: 700},
            footer: {
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const stageTypes = [
        {id: "league", name: t("addStageModal.stageTypes.league")},
        {id: "round_robin", name: t("addStageModal.stageTypes.roundRobin")},
        {id: "brackets", name: t("addStageModal.stageTypes.brackets")},
        {id: "training", name: t("addStageModal.stageTypes.training")},
    ];

    const gamesMode = [
        {id: "single_game", name: t("addStageModal.gameModes.singleGame")},
        {id: "double_game", name: t("addStageModal.gameModes.doubleGame")},
        {id: "double_header", name: t("addStageModal.gameModes.doubleHeader")},
        {id: "series_3", name: t("addStageModal.gameModes.series3")},
        {id: "series_5", name: t("addStageModal.gameModes.series5")},
        {id: "series_7", name: t("addStageModal.gameModes.series7")},
    ];

    const refreshStages = (result) => {
        if (result.data?.createStage) {
            // close modal (your existing mechanism)
            if (modalElem?.current) modalElem.current.click();

            toast.success(t("addStageModal.toasts.createdSuccess"), {
                autoClose: 3000,
                toastId: "stage_added",
                position: "top-center",
                closeOnClick: true,
            });

            setStages([
                ...stages,
                {
                    id: result.data.createStage.stage.id,
                    name: result.data.createStage.stage.name,
                    scheduleType: result.data.createStage.stage.scheduleType,
                    hasResults: false,
                },
            ]);

            if (stageNameInput.current) stageNameInput.current.value = "";
            if (stageTypeInput.current) stageTypeInput.current.value = "";
            if (gamesModeInput.current) gamesModeInput.current.value = "";
            if (minGamesRequiredInput.current) minGamesRequiredInput.current.value = "0";
            if (qtyTeamsQualified.current) qtyTeamsQualified.current.value = "";
            if (qtyTeamsAdditional.current) qtyTeamsAdditional.current.value = "";
            setStageType("");

            setTimeout(() => window.location.reload(), 3000);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const parsedMinGamesRequired = Number(minGamesRequiredInput.current?.value ?? 0);
        if (!Number.isInteger(parsedMinGamesRequired) || parsedMinGamesRequired < 0) {
            toast.error(t("addStageModal.toasts.minGamesInvalid"), {
                autoClose: 3000,
                toastId: "stage_min_games_invalid",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        const variables = {
            name: stageNameInput.current.value,
            scheduleType: stageType,
            seasonId: activeSeasonData?.id,
            minGamesRequired: parsedMinGamesRequired,
        };

        if (stageType && stageType !== "training") {
            variables.gamesMode = gamesModeInput.current.value;
            variables.qtyTeamsQualified = Number(qtyTeamsQualified.current.value || 0);
            variables.qtyTeamsAdditional = Number(qtyTeamsAdditional.current.value || 0);
        }

        addStage({variables})
            .then((result) => refreshStages(result))
            .catch((err) => {
                toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                    autoClose: 3000,
                    toastId: "stage_error",
                    position: "top-center",
                    closeOnClick: true,
                });
            });
    };

    const showGameFields = stageType && stageType !== "training";

    return (
        <div className="modal fade" id="add-stage-modal" tabIndex="-1" role="dialog" aria-hidden="true">
            <div className="modal-dialog modal-lg" role="document" style={{maxWidth: 840}}>
                <div className="modal-content" style={{borderRadius: 18, overflow: "hidden"}}>
                    <Form method="post" onSubmit={handleSubmit}>
                        {/* Header (TeamList style) */}
                        <div style={ui.header}>
                            <div style={ui.titleWrap}>
                                <span style={ui.badge}>{t("addStageModal.badge")}</span>
                                <div style={{minWidth: 0}}>
                                    <h4 style={ui.title}>{t("addStageModal.title")}</h4>
                                    <p style={ui.sub}>
                                        {t("addStageModal.createFor")}{" "}
                                        <b>{activeSeasonData?.name || t("addStageModal.thisSeason")}</b>
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                style={ui.closeBtn}
                                data-dismiss="modal"
                                aria-label={t("addStageModal.close")}
                                title={t("addStageModal.close")}
                            >
                                <i className="fas fa-times"/>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="modal-body" style={ui.body}>
                            <div style={ui.sectionCard}>
                                <div className="row">
                                    <div className="col-sm-6">
                                        <div className="form-group mb-0">
                                            <div style={ui.label}>{t("addStageModal.fields.stageName")}</div>
                                            <input
                                                type="text"
                                                className="form-control"
                                                required
                                                ref={stageNameInput}
                                                placeholder={t("addStageModal.placeholders.stageName")}
                                                style={ui.input}
                                            />
                                            <div style={ui.helper}>{t("addStageModal.helpers.stageName")}</div>
                                        </div>
                                    </div>

                                    <div className="col-sm-6">
                                        <div className="form-group mb-0">
                                            <div style={ui.label}>{t("addStageModal.fields.stageType")}</div>
                                            <select
                                                className="form-control"
                                                required
                                                ref={stageTypeInput}
                                                defaultValue=""
                                                onChange={(e) => setStageType(e.target.value)}
                                                style={ui.input}
                                            >
                                                <option
                                                    value="">{t("addStageModal.placeholders.chooseStageType")}</option>
                                                {stageTypes.map((type) => (
                                                    <option key={type.id} value={type.id}>
                                                        {type.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <div style={ui.helper}>
                                                {t("addStageModal.helpers.stageType")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {showGameFields && (
                                <div style={ui.sectionCard}>
                                    <div className="row">
                                        <div className="col-sm-6">
                                            <div className="form-group mb-3">
                                                <div style={ui.label}>{t("addStageModal.fields.gameMode")}</div>
                                                <select
                                                    className="form-control"
                                                    ref={gamesModeInput}
                                                    required={showGameFields}
                                                    defaultValue=""
                                                    style={ui.input}
                                                >
                                                    <option
                                                        value="">{t("addStageModal.placeholders.chooseGameMode")}</option>
                                                    {gamesMode.map((type) => (
                                                        <option key={type.id} value={type.id}>
                                                            {type.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-sm-6">
                                            <div className="form-group mb-0">
                                                <div style={ui.label}>{t("addStageModal.fields.minGamesRequired")}</div>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    required
                                                    ref={minGamesRequiredInput}
                                                    defaultValue={0}
                                                    placeholder={t("addStageModal.placeholders.minGamesRequired")}
                                                    style={ui.input}
                                                    min={0}
                                                    step={1}
                                                />
                                                <div
                                                    style={ui.helper}>{t("addStageModal.helpers.minGamesRequired")}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <Divider style={{margin: "20px 0", borderColor: "rgba(0,0,0,0.06)"}}/>

                                    <div className="row">
                                        <div className="col-sm-6">
                                            <div className="form-group mb-0">
                                                <div style={ui.label}>
                                                    {t("addStageModal.fields.qualifiedTeamsPerGroup")}
                                                </div>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    ref={qtyTeamsQualified}
                                                    required={showGameFields}
                                                    style={ui.input}
                                                    min={0}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-6">
                                            <div className="form-group mb-0">
                                                <div style={ui.label}>
                                                    {t("addStageModal.fields.additionalTeams")}
                                                </div>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    ref={qtyTeamsAdditional}
                                                    required={showGameFields}
                                                    style={ui.input}
                                                    min={0}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer (TeamList style) */}
                        <div className="modal-footer" style={ui.footer}>
                            <button type="button" style={ui.btn} data-dismiss="modal">
                                <i className="fas fa-times"/>
                                {t("addStageModal.close")}
                            </button>
                            <button type="submit" style={{...ui.btn, ...ui.btnPrimary}}>
                                <i className="fas fa-check"/>
                                {t("addStageModal.save")}
                            </button>
                        </div>
                    </Form>
                </div>
            </div>

            {/* Focus polish (scoped) */}
            <style>{`
        #add-stage-modal .modal-content button:focus,
        #add-stage-modal .modal-content input:focus,
        #add-stage-modal .modal-content select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }
      `}</style>
        </div>
    );
}
